<template>
  <div>
    <md-dialog
      :md-active.sync="open"
      :md-click-outside-to-close="quickEscape"
      :md-close-on-esc="quickEscape"
      class="md-dialog md-alignment-centered"
    >
      <md-dialog-title>
        <md-icon class="title-icon">
          map
        </md-icon>
        {{ dialogTitle }} Scenario
      </md-dialog-title>
      <md-dialog-content class="layout-no-padding">
        <md-card-media-cover
          v-if="showInstructions"
          :md-solid="showInstructions"
        >
          <md-card-media>
            <img :src="image">
          </md-card-media>

          <md-card-area>
            <md-card-content v-if="showInstructions">
              <span v-html="$Region.modelling_description" />
            </md-card-content>
          </md-card-area>
        </md-card-media-cover>
        <md-card-content>
          <div class="text-align-center">
            <md-button
              class="md-info md-sm md-round"
              @click="showInstructions = !showInstructions"
            >
              <span v-if="showInstructions">Hide Instructions</span>
              <span v-else>Show Instructions</span>
            </md-button>
          </div>
          <simple-wizard
            :show-header="false"
            :show-navigation="false"
            :finish-button-text="submitButtonText"
            :show-cancel="true"
            :on-cancel="CLOSE_EDIT_SCENARIO_DIALOG"
          >
            <wizard-tab :before-change="() => submitScenario()">
              <!-- SCENARIO TYPE -->
              <div class="md-layout">
                <div class="md-layout-item md-size-100">
                  <h5 class="info-text">
                    {{ scenarioTypeText }}
                  </h5>
                </div>
                <div
                  v-for="item in scenarioTypes"
                  :key="item.id"
                  class="md-layout-item md-xsmall-size-100 md-small-size-100"
                  :class="[{'md-size-50': scenarioTypes.length === 2}, {'md-size-33': scenarioTypes.length === 3}, {'md-size-25': scenarioTypes.length === 4}]"
                >
                  <icon-checkbox
                    v-model="item.checked"
                    :disabled="scenarioTypeDisabled"
                    :icon="item.icon"
                    :title="item.name"
                    @input="toggleScenarioType(item.name)"
                    @disabled-warning="showDisabledWarning()"
                  />
                </div>
                <!-- END SCENARIO TYPE -->
                <!-- TITLE -->
                <div v-if="editMode === 'edit'">
                  <div class="md-layout-item md-size-100 md-layout">
                    <label class="md-layout-item md-size-15 md-xsmall-size-100 md-small-size-30 md-medium-size-20 md-form-label">Scenario Title</label>
                    <div class="md-layout-item md-size-85 md-xsmall-size-100 md-small-size-70 md-medium-size-80">
                      <md-field>
                        <md-input
                          v-model="scenarioTitle"
                          type="text"
                        />
                        <span class="md-helper-text">Use a concise, short title like "Urban Densification"</span>
                      </md-field>
                    </div>
                  </div>
                  <!-- DESCRIPTION -->
                  <div class="md-layout-item md-size-100 md-layout">
                    <label class="md-layout-item md-size-15 md-xsmall-size-100 md-small-size-30 md-medium-size-20 md-form-label">Scenario Description</label>
                    <div class="md-layout-item md-size-85 md-xsmall-size-100 md-small-size-70 md-medium-size-80">
                      <md-field>
                        <label>Provide a brief explanation outlining your goals</label>
                        <md-textarea
                          v-model="scenarioDescription"
                          type="text"
                        />
                        <span class="md-helper-text" />
                      </md-field>
                    </div>
                  </div>
                </div>
              </div>
            </wizard-tab>
          </simple-wizard>
        </md-card-content>
      </md-dialog-content>
    </md-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { types } from "@/store/types";
import { SimpleWizard, WizardTab } from '@/components';
import { IconCheckbox } from '@/components';
import { ScenarioImages } from "../Components/mixins/ScenarioImages";
import { Scenario } from "../Components/mixins/Scenario";
import swal from "sweetalert2";

export default {
  name: "EditScenarioDialog",

  components: {
    SimpleWizard,
    WizardTab,
    IconCheckbox
  },
  mixins: [ScenarioImages, Scenario],

  props: {
    editMode: {
      type: String,
      default: "create", // whether in create or edit mode
      validator: function (value) {
        // must be one of ["create", "edit"] 
        return ["create", "edit"].indexOf(value) !== -1;
      }
    },
    open: {
      type: Boolean,
      default: false,
    },
    quickEscape: {
      type: Boolean,
      default: true,
    }
  },

  data() {
    return {
      showInstructions: false,
      scenarioTitle: null,
      defaultTitle: this.$Region.default_scenario_title,
      prv_scenarioTitle: null, // for storing temp titles when user toggles between system scenario types vs user scenarios
      scenarioDescription: null,
      prv_scenarioDescription: null, // for storing temp descriptions when user toggles between system scenario types vs user scenarios
      scenarioTypes: [
        // additional scenarios types automatically initialized from region-config.js via initScenarioTypes()
        {
          name: "Land Use",
          checked: false,
          icon: "fas fa-pen"
        }
      ],
      scenario: null
    };
  },

  computed: {
    dialogTitle() {
      // what to show at the top of the card
      return (this.editMode === 'create') ? "Create New" : "Edit";
    },
    scenarioTypeDisabled() {
      // scenario type can not be changed in edit mode - once a scenario has been created, it must remain that type
      return (this.editMode === 'create') ? false : true;
    },
    scenarioTypeText() {
      return this.editMode === 'create' ? `What type of simulation would you like to create?` : `Simulation Type`;
    },
    submitButtonText() {
      // text to put on the submit button
      return (this.editMode === 'create') ? "Create" : "Update";
    },
    scenarioType: {
      // currently selected scenarioType
      // getter
      get: function () {
        try {
          return this.scenarioTypes.filter(st => st.checked)[0].name;
        } catch (err) {
          return null;
        }
      },
      // setter
      set: function (name) {
        this.toggleScenarioType(name);
      }
    },
    systemScenario: function () {
      // return matching system scenario
      return this.$Region.system_scenarios.find(
        scen => scen.type == this.scenarioType
      );
    },
    image: function () {
      return this.images[this.scenarioType];
    },
    scenarioValidated() {
      // validate that required fields are filled in
      if (!this.scenarioTitle) {
        this.$notify({
          message: "Please Enter a Scenario Title",
          icon: "error_outline",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger"
        });
        return false;
      } else if (this.editMode === 'edit' && !this.scenarioDescription) {
        this.$notify({
          message: "Please Enter a Scenario Description",
          icon: "error_outline",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger"
        });
        return false;
      }
      return true;
    }
  },

  beforeMount() {
    // set scenario types
    this.initScenarioTypes();
    if (this.editMode === 'edit') {
      this.scenarioTitle = this.ACTIVE_SCENARIO.name;
      this.scenarioDescription = this.ACTIVE_SCENARIO.description;
    }
  },

  mounted() {
    // if this is the user's first scenario show them a dialog
    if (this.USER_SCENARIOS.length === 0) {
      swal.fire({
        title: "Your First Scenario!",
        html: this.$Region.modelling_description,
        icon: "info",
        iconHtml: `<i class="fas fa-info"></i>`,
        customClass: {
          confirmButton: "md-button md-success",
        },
        buttonsStyling: false
      });
    }
  },

  methods: {
    ...mapActions([
      types.actions.CLOSE_EDIT_SCENARIO_DIALOG,
      types.actions.CLEAR_ACTIVE_HINT,
      types.actions.INIT_NEW_SCENARIO,
      types.actions.UPDATE_ACTIVE_SCENARIO,
      types.actions.CLEAR_INDICATOR_VALS,
      types.actions.SET_EXPLORE_MODE,
      types.actions.TOGGLE_REFRESH_REGIONS_LAYER,
      types.actions.SET_SCENARIO_WIZARD_TAB,
      types.actions.SET_SHOW_PLANNING_LAYER
    ]),

    initScenarioTypes() {
      // grab the available scenario types from the region-config.js
      if (
        this.$Region.system_scenarios &&
        this.$Region.system_scenarios.length
      ) {
        this.scenarioTypes = this.$Region.system_scenarios.map(type => {
          return {
            name: type.type,
            checked: false,
            icon: type.icon
          };
        }).concat(this.scenarioTypes);
      }
      // add id's to be used as keys by vue
      this.scenarioTypes.forEach((type, i) => {
        type.id = i;
      });
      // check first one by default
      this.scenarioTypes[0].checked = true;
      if (this.systemScenario) {
        this.scenarioTitle = this.systemScenario.name;
        this.scenarioDescription = this.systemScenario.description;
      }
    },
    toggleScenarioType(name) {
      // scenariotype radio button toggling along with some auto-scenario titles and descriptions
      // tab names are handled by computed
      // next vs run simulation buttons are handled in template by v-if
      if (
        !this.$Region.system_scenarios.some(
          scen => scen.name == this.scenarioTitle
        )
      ) {
        // save previous scenarioTitle incase user toggles back later, only if it was NOT an auto-generated one from a sys scenario
        this.prv_scenarioTitle = this.scenarioTitle;
        this.prv_scenarioDescription = this.scenarioDescription;
      }
      // toggle the scenarioType
      this.scenarioTypes.forEach(st => {
        if (st.name !== name) {
          st.checked = false;
        } else if (!st.checked) {
          st.checked = true;
        }
      });
      // update name and description from region-config
      if (this.systemScenario) {
        // grab the default scenario name and description
        this.scenarioTitle = this.systemScenario.name;
        this.scenarioDescription = this.systemScenario.description;
      } else {
        // reset to whatever description user had previously set
        this.scenarioTitle = this.prv_scenarioTitle;
        this.scenarioDescription = this.prv_scenarioDescription;
      }
    },
    submitScenario() {
      // when submit button is clicked - either save existing or create new
      if (this.editMode === 'create' && !this.scenarioTitle) {
        this.scenarioTitle = this.defaultTitle;
      }
      if (this.scenarioValidated) {
        if (this.editMode === 'create') {
          this.createScenario();
        } else {
          this.updateEditScenario();
        }
      }
    },
    createScenario() {
      // validate and save scenario to store for further input on next screen
      if (this.scenarioValidated) {
        // clear any potential sharing query in the url
        this.$router.replace({ query: null });
        this.INIT_NEW_SCENARIO({ vm: this });
        // the base raster used as a reference is not necessarily same time as the region config senario start/end time.
        // Needs to be temporarily set to the base raster time for goal and indicator calculations during creation.
        let startTime = this.$Region.base_raster_time;
        let endTime = this.$Region.base_raster_time;
        if (this.systemScenario) {
          // system scenario settings - goals and indicator values are not displayed for system scenarios so load the proper values
          startTime = this.systemScenario.startTime;
          endTime = this.systemScenario.endTime;
        }
        this.scenario = {
          name: this.scenarioTitle,
          description: this.scenarioDescription,
          type: this.scenarioType,
          startTime: startTime,
          endTime: endTime
        };
        if (this.scenarioType === 'Historic') {
          // ensure climate is set to none for Historic
          // climate scenario could be set by $Region.climateDefaultIndex
          // but if ths scenario type is historic, this will cause scenario computing conflicts in lambda
          this.scenario.climate = 'none';
        }
        this.UPDATE_ACTIVE_SCENARIO(this.scenario);
        this.CLEAR_ACTIVE_HINT();
        this.CLEAR_INDICATOR_VALS();
        this.SET_EXPLORE_MODE('create');
        this.CLOSE_EDIT_SCENARIO_DIALOG();
        this.SET_SCENARIO_WIZARD_TAB('tab_region');
        this.SET_SHOW_PLANNING_LAYER(false);
        if (this.$route.name !== 'Explore') {
          this.$router.push({
            name: "Explore"
          });
        } else {
          this.TOGGLE_REFRESH_REGIONS_LAYER();
        }
      }
    },
    updateEditScenario() {
      this.UPDATE_ACTIVE_SCENARIO({ name: this.scenarioTitle, description: this.scenarioDescription });
      this.saveScenarioUpdatesToDB(this.ACTIVE_SCENARIO, ["name", "description"], false);
      this.CLOSE_EDIT_SCENARIO_DIALOG();
    },
    showDisabledWarning() {
      // can't edit scenario types for existing scenarios
      this.$notify({
        message: "Sorry, you can't change the scenario type for a scenario once it has been created.",
        icon: "error_outline",
        horizontalAlign: "right",
        verticalAlign: "top",
        type: "danger"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
/deep/ .layout-no-padding {
  padding: 0;
}
.text-align-center {
  display: inline-block;
  width: 100%;
  text-align: center;
}
.button-align-right {
  display: inline-block;
  width: 100%;
  text-align: right;
}

.md-card-area {
  height: 100%;
  overflow-y: auto;
}

.md-dialog-content {
  padding: 0;
}

.md-dialog-title {
  position: relative;
  background-color: $grey-800;
  color: $white-color;
  padding-top: 20px;
  padding-bottom: 15px;
  margin: 0;
}
.title-icon {
  color: $white-color !important;
  margin-top: -0.15em;
  margin-right: 10px;
}
</style>
